import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { monospaceStyle } from "../../theme";
import { EntityRef } from "../../types/productPlane";
import DerivedTag from "../Detail/DerivedTag";
import { useHeadingSlot } from "../Detail/DetailSections";
import EntityLink from "../Detail/EntityLink";
import RowToggle, { NoToggle } from "../Detail/RowToggle";
import SearchField from "../Detail/SearchField";
import SourcesChip from "../Detail/SourcesChip";
import { columnHeader, quiet, tableFrame } from "../Detail/claimCells";
import { SEARCH_FROM } from "./ProductRelationTable";
import { RelationRow } from "./productRelations";

export interface ComponentTableProps {
  // The components, each with the broad kind of its class
  rows: RelationRow[];
  // A plain table for a printed page: every group open, nothing to open or
  // filter, the summary beneath the name
  print?: boolean;
}

// A table of this many components or fewer opens with every group open;
// a longer one with every group closed, so that it reads as its kinds
export const OPEN_UP_TO = 15;

// The group of the components with no class
export const NO_GROUP = "No class";

export interface ComponentGroup {
  key: string;
  name: string;
  rows: RelationRow[];
  // The components it holds, an unnamed row counting each it stands for
  count: number;
}

const componentCount = (rows: RelationRow[]): number =>
  rows.reduce((count, row) => count + (row.count ?? 1), 0);

/**
 * The components grouped by the broad kind of their class: the group with
 * the most components first, the components with no class last.
 */
export const componentGroups = (rows: RelationRow[]): ComponentGroup[] => {
  const byGroup = new Map<string, { group: EntityRef | null; rows: RelationRow[] }>();
  for (const row of rows) {
    const key = row.group?.id ?? "";
    const entry = byGroup.get(key) ?? { group: row.group ?? null, rows: [] };
    entry.rows.push(row);
    byGroup.set(key, entry);
  }
  return Array.from(byGroup, ([key, { group, rows: groupRows }]) => ({
    key,
    name: group?.name ?? NO_GROUP,
    rows: groupRows,
    count: componentCount(groupRows),
  })).sort(
    (left, right) =>
      Number(left.key === "") - Number(right.key === "") ||
      right.count - left.count ||
      left.name.localeCompare(right.name),
  );
};

// The words a component is searched by
const searchText = (row: RelationRow, group: string): string =>
  [row.entity.name, row.note, row.detail, row.summary, row.category?.name, group]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

// The cells of a row cut to one line on the screen, whole on hover
const oneLine = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } as const;

// The columns besides the toggle: their heads and widths
const COLUMNS = [
  { heading: "Component", width: "42%" },
  { heading: "Function", width: "30%" },
  { heading: "Latest source", width: 120 },
  { heading: "Sources", width: 110, align: "right" as const },
];

// The name of a component, how many unnamed ones it stands for, its
// derivation, and the line that says more of it beneath
const NameCell: React.FC<{ row: RelationRow; wrap: boolean }> = ({ row, wrap }) => (
  <Box sx={{ minWidth: 0 }}>
    <Box title={wrap ? undefined : (row.entity.name ?? undefined)} sx={wrap ? undefined : oneLine}>
      <EntityLink id={row.entity.id} type={row.entity.type} name={row.entity.name} />
      {row.count !== undefined && row.count > 1 && (
        <Box component="span" sx={{ ...monospaceStyle, ...quiet, ml: 0.75, fontSize: "0.75rem" }}>
          ×{row.count}
        </Box>
      )}
      {row.derived && (
        <>
          {" "}
          <DerivedTag basis={row.derived.basis} />
        </>
      )}
    </Box>
    {row.note && (
      <Box sx={{ ...quiet, fontSize: "0.75rem", ...(wrap ? {} : oneLine) }}>{row.note}</Box>
    )}
  </Box>
);

/**
 * A product's components as one table, grouped by the broad kind of their
 * class ("Navigation, positioning & timing"): a row per group with its
 * count opens and closes the components beneath it. A short table opens
 * with every group open, a long one with every group closed. A component
 * opens on its sentence; above the table a longer one offers a search,
 * which opens every group it finds a component in, and a chip sets the
 * derived links aside.
 */
const ComponentTable: React.FC<ComponentTableProps> = ({ rows, print = false }) => {
  const groups = useMemo(() => componentGroups(rows), [rows]);
  const headingSlot = useHeadingSlot();
  const [openGroups, setOpenGroups] = useState<Set<string>>(
    () => new Set(rows.length <= OPEN_UP_TO ? groups.map((group) => group.key) : []),
  );
  const [openRows, setOpenRows] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [statedOnly, setStatedOnly] = useState(false);

  const hasDerived = rows.some((row) => row.derived);
  const term = search.trim().toLowerCase();
  const shown = groups
    .map((group) => ({
      ...group,
      rows: group.rows.filter(
        (row) =>
          (!statedOnly || !row.derived) && (!term || searchText(row, group.name).includes(term)),
      ),
    }))
    .filter((group) => group.rows.length > 0);

  const flip = (set: Set<string>, key: string): Set<string> => {
    const next = new Set(set);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    return next;
  };

  if (rows.length === 0) {
    return null;
  }

  // The search of a longer table, in the line of the part's heading where
  // it has one, so that no line of its own stands between them
  const searchField = !print && rows.length >= SEARCH_FROM && (
    <SearchField
      value={search}
      onChange={setSearch}
      label="Search components"
    />
  );
  const searchInToolbar = searchField && !headingSlot;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {searchField && headingSlot && createPortal(searchField, headingSlot)}
      {!print && (hasDerived || searchInToolbar) && (
        <Box
          role="toolbar"
          aria-label="Filter components"
          sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75 }}
        >
          {hasDerived && (
            <Chip
              label="Stated only"
              size="small"
              clickable
              color={statedOnly ? "primary" : "default"}
              variant={statedOnly ? "filled" : "outlined"}
              aria-pressed={statedOnly}
              onClick={() => setStatedOnly((value) => !value)}
            />
          )}
          {searchInToolbar && searchField}
        </Box>
      )}
      <Box sx={print ? undefined : tableFrame}>
        <Table
          size="small"
          aria-label="Components"
          sx={{
            tableLayout: "fixed",
            "& .MuiTableCell-root": { borderColor: "divider" },
          }}
        >
          <TableHead>
            <TableRow>
              {!print && <TableCell sx={{ width: 36, pr: 0 }} />}
              {COLUMNS.map((column, index) => (
                <TableCell
                  key={index}
                  align={column.align}
                  sx={{ ...columnHeader, width: column.width }}
                >
                  {column.heading}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length + 1} sx={quiet}>
                  No components match the filters
                </TableCell>
              </TableRow>
            )}
            {shown.map((group) => {
              // A search opens every group it finds a component in
              const open = print || Boolean(term) || openGroups.has(group.key);
              return (
                <React.Fragment key={group.key}>
                  <TableRow
                    aria-label={`${group.name}, ${group.count}`}
                    onClick={print ? undefined : () => setOpenGroups((set) => flip(set, group.key))}
                    sx={{
                      cursor: print ? undefined : "pointer",
                      // A faint band sets the group apart from its rows
                      backgroundColor: (theme) => alpha(theme.palette.chart.ink, 0.03),
                    }}
                  >
                    <TableCell colSpan={COLUMNS.length + (print ? 0 : 1)} sx={{ py: 0.75 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                        {!print && (
                          // The row opens on a click anywhere; the toggle
                          // is the control a keyboard reaches
                          <Box onClick={(event) => event.stopPropagation()}>
                            <RowToggle
                              open={open}
                              label={open ? `Hide ${group.name}` : `Show ${group.name}`}
                              onToggle={() => setOpenGroups((set) => flip(set, group.key))}
                            />
                          </Box>
                        )}
                        <Typography component="span" sx={{ fontSize: "0.8125rem", fontWeight: 600 }}>
                          {group.name}
                        </Typography>
                        <Box component="span" sx={{ ...monospaceStyle, ...quiet, fontSize: "0.75rem" }}>
                          {group.count}
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                  {open &&
                    group.rows.map((row) => {
                      const rowOpen = openRows.has(row.key);
                      return (
                        <React.Fragment key={row.key}>
                          <TableRow sx={{ verticalAlign: "middle" }}>
                            {!print && (
                              <TableCell sx={{ pr: 0 }}>
                                {row.summary ? (
                                  <RowToggle
                                    open={rowOpen}
                                    label={rowOpen ? "Collapse" : "Expand"}
                                    onToggle={() => setOpenRows((set) => flip(set, row.key))}
                                  />
                                ) : (
                                  <NoToggle />
                                )}
                              </TableCell>
                            )}
                            <TableCell sx={{ pl: print ? undefined : 3 }}>
                              <NameCell row={row} wrap={print} />
                              {print && row.summary && (
                                <Box sx={{ ...quiet, fontSize: "0.8125rem", mt: 0.5 }}>
                                  {row.summary}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell>
                              <Box
                                title={print ? undefined : (row.detail ?? undefined)}
                                sx={{ ...quiet, ...(print ? {} : oneLine) }}
                              >
                                {row.detail}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Box component="span" sx={{ ...monospaceStyle, ...quiet, whiteSpace: "nowrap" }}>
                                {row.latest}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              {print ? row.sources.length : <SourcesChip sources={row.sources} />}
                            </TableCell>
                          </TableRow>
                          {!print && rowOpen && (
                            <TableRow>
                              <TableCell />
                              <TableCell colSpan={COLUMNS.length} sx={{ pl: 3 }}>
                                <Typography variant="body2" sx={{ whiteSpace: "normal", py: 0.5, maxWidth: 760 }}>
                                  {row.summary}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};

export default ComponentTable;
